import React, { useState, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast, ToastContainer } from "react-toastify";
import useFormPersist from "react-hook-form-persist";
import { ViewedExecuteSopsConfig } from "./ViewedExecuteSopsConfig";
import { useTranslation } from "react-i18next";
import {
  getData,
  postDataWithBody,
  putDataWithBody,
  editData,
  getDataById,
} from "../../../../../Services/Services";

import {
  getTaskConfigsURL,
  getAllActiveRole,
  createTaskURL,
  findTaskById,
  getTaskListURL,
  updateTaskURL,
  incidentList,
  sopList,
  SopTaskTableData,
  sopResourceList,
  sopGetIncident,
  SopUserData,
} from "../../../../../Services/EndPoints";

import Loader from "react-spinner-loader";
import { downloadFile } from "../../../../../Components/UiComponents/utils";
import { PaperClipIcon } from "../../../../../Components/IconComponents";
import { useKeycloak } from "@react-keycloak/web";
import Moment from "react-moment";
import ViewedExecuteSopsExpandableTable from "./ViewedExecuteSopsExpandableTable";
import ViewedSopInstance from "./ViewedSopInstance";
import ViewedSopInstanceMap from "./ViewedSopInstanceMap";

const ViewedExecuteSopsForm = () => {
  const { t } = useTranslation();
  const [sopTableData, setSopTableData] = useState([]);
  const [userTaskData, setUserTaskData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [isFieldsDisabled, setIsFieldsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [sopFormConfig, setSopFormConfig] = useState(ViewedExecuteSopsConfig);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [taskConfigs, setTaskConfigs] = useState([]);
  const [viewTaskDetails, setViewTaskDetails] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);

  const { state } = useLocation();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();

  const taskId = state?.data?.id;
  const mediaFilePath = state?.data?.media_file_path;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(),
    mode: "all",
  });

  // Watch for changes in dropdown values
  const selectedSopId = watch("task_name");
  const selectedIncidentId = watch("incident_type_id");

  useEffect(() => {
    loadInitialData();
    fetchSopUserData();
  }, []);

  useEffect(() => {
    if (selectedSopId) {
      fetchTaskList();
      fetchSopResourceList();

      // Update name field with SOP selection
      const selectedSop = availableRoles.find(
        (sop) => sop.id === parseInt(selectedSopId)
      );
      if (selectedSop) {
        const now = new Date();

        // Format date as DD:MM:YYYY
        const day = String(now.getDate()).padStart(2, "0");
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const year = now.getFullYear();
        const formattedDate = `${day}:${month}:${year}`;

        // Format time in 24-hour format (HH:mm)
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const formattedTime = `${hours}:${minutes}`;

        // Combine SOP name with date and time
        const nameValue = `${selectedSop.name}_${formattedDate} ${formattedTime}`;

        setValue("assign_to_id", nameValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [selectedSopId]);

  useEffect(() => {
    if (selectedIncidentId) {
      fetchSopIncident();
    }
  }, [selectedIncidentId]);

  const fetchTaskList = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(SopTaskTableData, selectedSopId);
      const sortedTasks = response
        ? response.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
        : [];

      setUserTaskData(sortedTasks);
      setRowCount(sortedTasks.length);
    } catch (error) {
      console.error("Error fetching task list:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSopResourceList = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(sopResourceList, selectedSopId);
      console.log("SOP Resource Data:", response);
    } catch (error) {
      console.error("Error fetching SOP resources:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSopIncident = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(sopGetIncident, selectedIncidentId);
      console.log("SOP Incident Data:", response);
    } catch (error) {
      console.error("Error fetching SOP incident:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSopUserData = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(SopUserData, selectedSopId);
      console.log("SOP User Data:", response);
    } catch (error) {
      console.error("Error fetching SOP user data:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadInitialData = async () => {
    setIsLoading(true);
    await fetchTaskConfigs();
    await fetchIncidentTypes();
    await fetchSopList();
    await fetchViewData();
    setIsLoading(false);

    if (taskId) {
      populateFormForEdit(taskId);
    }
  };

  const fetchIncidentTypes = async () => {
    const incidentTypes = await getData(incidentList);
    const formattedTypes = incidentTypes.map((type) => ({
      label: type.name,
      value: type.id,
    }));

    updateFormConfig(0, 0, formattedTypes);
  };

  const fetchSopList = async () => {
    const sops = await getData(sopList);
    setAvailableRoles(sops);

    const formattedSops = sops.map((sop) => ({
      label: sop.name,
      value: sop.id,
    }));

    updateFormConfig(0, 1, formattedSops);
  };

  const updateFormConfig = (configIndex, fieldIndex, options) => {
    const updatedConfig = [...sopFormConfig];
    updatedConfig[configIndex].fields[fieldIndex].options = options;
    setSopFormConfig(updatedConfig);
  };

  const fetchTaskConfigs = async () => {
    const response = await postDataWithBody(
      getTaskConfigsURL,
      ["TSK_PRIOR", "INCDNT_CAT", "TSK_STAT", "TSK_TYP"],
      {}
    );
    setTaskConfigs(response);
  };

  const fetchViewData = async () => {
    const viewData = await getData(getTaskListURL);
    if (taskId) {
      populateFormForEdit(taskId);
    }
  };

  const populateFormForEdit = async (id) => {
    const response = await postDataWithBody(findTaskById, [id], {});
    const taskData = response[0];
    setViewTaskDetails(taskData);
    reset(taskData);
  };

  const handleclick = () =>{
    navigate("../sops-instance");
  }

  const generateFormFields = (fields, register, errors, control) => {
    return (
      <>
        <div className="form-body">
          <div className="registration__form--body">
            <div className="sop-instance-table">
              <div class="row">
                <div class="col-sm-3">
                  <div className="sop_instance_info">ID</div>
                </div>
                <div class="col-sm-3">
                  <div className="sop_instance_info">Instance Name</div>
                </div>
                <div class="col-sm-3">
                  <div className="sop_instance_info">Status</div>
                </div>
              </div>
              <div class="row">
                <div class="col-sm-3">
                  <div>258</div>
                </div>
                <div class="col-sm-3">
                  <div>Gas Leakage Overview name SOP 08:01:2025 16:24</div>
                </div>
                <div class="col-sm-3">
                  <div>Started</div>
                </div>
                <div class="col-sm-3">
                  <button class="btn btn-deploy" onClick={handleclick}>Assign User Task</button>
                  <button class="btn btn-close-sop">Close SOP</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const generateTabsContent = (tabData) => {
    tabData?.forEach((tab) => {
      tab.component = generateFormFields(
        tab?.fields,
        register,
        errors,
        control
      );
    });
    return tabData;
  };

  const handleFormSubmit = async (data, e) => {
    setIsLoading(true);
    setIsSubmitting(true);
    e.preventDefault();

    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === "task_media_file" && value?.length > 0) {
        Array.from(value).forEach((file) => formData.append(key, file));
      } else {
        formData.append(key, value);
      }
    });

    formData.append("status", true);
    if (taskId) formData.append("id", taskId);

    try {
      const headers = { "Content-Type": "multipart/form-data" };
      const response = taskId
        ? await putDataWithBody(updateTaskURL, formData, headers)
        : await postDataWithBody(createTaskURL, formData, headers);

      if (response.status === "success") {
        toast.success(response.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        navigate("/planning/view-task");
      } else {
        toast.error(response.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(t("submission_error"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  const handleExecuteSop = () => {
    navigate("../viewed-sops-instance");
  };

  const tableColumns = React.useMemo(
    () => [
      {
        Header: t("viewedexecutesopstableheaders.id"),
        accessor: "name",
        disableSortBy: true,
      },
      {
        Header: t("viewedexecutesopstableheaders.name"),
        accessor: "task_type",
        disableSortBy: true,
        Cell: ({ value }) => (value === 0 ? "Activity" : "Notification"),
      },
      {
        Header: t("viewedexecutesopstableheaders.status"),
        accessor: "incidenttype",
        disableSortBy: true,
      },
      {
        Header: t("viewedexecutesopstableheaders.quantity"),
        accessor: "assignedto",
        disableSortBy: true,
      },
      {
        Header: t("viewedexecutesopstableheaders.action"),
        accessor: "",
        disableSortBy: true,
      },
      {
        Header: t("viewedexecutesopstableheaders.locateonmap"),
        accessor: "",
        disableSortBy: true,
      },
    ],
    [t]
  );

  const downloadAttachmentURL = `${process.env.REACT_APP_APP_SERVER_ADDRESS}/${process.env.REACT_APP_CORE_CONTEXT_PATH_NAME}/download/download_attachments/?directoryPath=${mediaFilePath}&fileName=Tasks.zip`;

  const tabsContent = generateTabsContent(sopFormConfig);

  return (
    <>
      <div className="mt-4">
        <Loader
          show={isLoading}
          type="body"
          stack="vertical"
          message="Loading Data"
        />
        <div className="form-body">
          <div className="registration__form--body">
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              name="CreateTaskRegForm"
            >
              <div>
                {tabsContent?.map((tab) => (
                  <div key={tab.id}>
                    {tab.id === activeTabIndex && <div>{tab.component}</div>}
                  </div>
                ))}
              </div>

              {taskId && mediaFilePath && (
                <div className="multi_file_uploaded cursor-pointer">
                  <div
                    onClick={() =>
                      downloadFile(
                        downloadAttachmentURL,
                        `Tasks${Date.now()}.zip`
                      )
                    }
                  >
                    <PaperClipIcon className="cursor-pointer" /> Attached files
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      <div>
        <Loader show={isLoading} type="body" message="Loading Data" />
        <div className="ViewedSopInstanceMap">
          <ViewedSopInstanceMap />
        </div>

        <ViewedExecuteSopsExpandableTable
          data={userTaskData}
          columns={tableColumns}
          numberOfRows={7}
          showExport={false}
          showFilter={false}
          showReset={false}
          onExpand={false}
          rowCount={rowCount}
          headerTableHeadStyle={{ background: "#18a3a0", color: "#FFFFFF" }}
          tableStyle={{
            border: "1px solid #f0f0f0",
            borderRadius: "12px",
          }}
        />
      </div>
    </>
  );
};

export default ViewedExecuteSopsForm;
